<template>
  <div v-if="callStatus === 'active' || callStatus === 'reconnecting' || callStatus === 'voicemail' || (callStatus === 'ringing' && !incomingCall)" class="acm-overlay">
    <div class="acm-card">
      <div class="acm-info">
        <v-avatar :size="72" color="#e5e5ea" class="acm-avatar">
          <v-img v-if="otherPartyAvatar" :src="otherPartyAvatar" />
          <v-icon v-else color="#8e8e93" size="32">mdi-account</v-icon>
        </v-avatar>
        <div class="acm-name">{{ otherPartyName }}</div>
        <div class="acm-status">{{ statusText }}</div>
      </div>
      <div class="acm-controls">
        <button
          class="acm-ctrl-btn"
          :class="{ active: isMuted }"
          @click="toggleMute"
          title="Mute"
        >
          <svg v-if="!isMuted" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <line x1="1" y1="1" x2="23" y2="23"/>
            <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
            <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.13 1.49-.35 2.17"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
          <span class="acm-ctrl-label">{{ isMuted ? 'Unmute' : 'Mute' }}</span>
        </button>
        <button
          class="acm-ctrl-btn"
          :class="{ active: isSpeakerOn }"
          @click="toggleSpeaker"
          title="Speaker"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <path v-if="isSpeakerOn" d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
            <path v-else d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
          </svg>
          <span class="acm-ctrl-label">Speaker</span>
        </button>
        <button class="acm-end-btn" @click="onEndCall" title="End call">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round">
            <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.1-1.1a2 2 0 0 1 2.11-.45 11.5 11.5 0 0 0 3.59.59 2 2 0 0 1 2 2v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 2.18 3.18 2 2 0 0 1 4 1h3.09a2 2 0 0 1 2 1.72 11.5 11.5 0 0 0 .59 3.59 2 2 0 0 1-.45 2.11z"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        </button>
      </div>
      <div v-if="callStatus === 'active' || callStatus === 'reconnecting' || callStatus === 'voicemail'" class="acm-timer">{{ formattedDuration }}</div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'ActiveCallModal',
  data() {
    return {
      elapsed: 0,
      timerInterval: null,
      missedTimeout: null,
    };
  },
  computed: {
    ...mapState('Call', ['activeCall', 'callStatus', 'isMuted', 'isSpeakerOn', 'incomingCall']),
    ...mapState('Authenticate', { owner_id: 'owner_id' }),
    otherPartyName() {
      if (!this.activeCall) return '';
      const isOutgoing = this.activeCall.caller_id === this.owner_id;
      return isOutgoing
        ? (this.activeCall.callee?.name || 'Unknown')
        : (this.activeCall.caller?.name || 'Unknown');
    },
    otherPartyAvatar() {
      if (!this.activeCall) return null;
      const isOutgoing = this.activeCall.caller_id === this.owner_id;
      return isOutgoing
        ? (this.activeCall.callee?.avatar || null)
        : (this.activeCall.caller?.avatar || null);
    },
    statusText() {
      if (this.callStatus === 'ringing') return 'Calling...';
      if (this.callStatus === 'reconnecting') return 'Reconnecting...';
      if (this.callStatus === 'voicemail') return 'Leave a message...';
      if (this.callStatus === 'active') return this.activeCall?.type === 'video' ? 'Video call' : 'Voice call';
      return '';
    },
    formattedDuration() {
      const mins = Math.floor(this.elapsed / 60);
      const secs = this.elapsed % 60;
      return `${mins}:${String(secs).padStart(2, '0')}`;
    },
  },
  watch: {
    callStatus(newStatus, oldStatus) {
      if (newStatus === 'active' && oldStatus !== 'active' && oldStatus !== 'reconnecting') {
        this.startTimer();
        this.clearMissedTimeout();
      }
      if (newStatus === 'voicemail' && oldStatus !== 'voicemail') {
        // Voicemail mode — start timer to show recording duration
        this.startTimer();
        this.clearMissedTimeout();
      }
      if (newStatus === 'ringing' && !this.incomingCall) {
        // Outgoing call — start 32s timeout for voicemail transition
        this.startVoicemailTimeout();
      }
      if (newStatus === 'idle' || newStatus === 'ended') {
        this.stopTimer();
        this.clearMissedTimeout();
      }
    },
  },
  methods: {
    ...mapActions('Call', ['endCall', 'toggleMute', 'toggleSpeaker']),
    onEndCall() {
      // In any state — end call disconnects Twilio, server handles finalization for voicemail
      this.endCall();
    },
    startTimer() {
      this.stopTimer();
      this.elapsed = 0;
      this.timerInterval = setInterval(() => {
        this.elapsed++;
      }, 1000);
    },
    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
      this.elapsed = 0;
    },
    startVoicemailTimeout() {
      this.clearMissedTimeout();
      // After 22s (slightly after Twilio's 20s dial timeout), transition to voicemail mode.
      // The Twilio connection stays alive — the server plays the greeting and records.
      const VOICEMAIL_TIMEOUT = 22000;
      let ringStart = parseInt(sessionStorage.getItem('call_ring_start'), 10);
      if (!ringStart) {
        ringStart = Date.now();
        sessionStorage.setItem('call_ring_start', String(ringStart));
      }
      const elapsed = Date.now() - ringStart;
      const remaining = Math.max(VOICEMAIL_TIMEOUT - elapsed, 0);

      if (remaining === 0) {
        // Already past timeout
        if (this.callStatus === 'ringing' && this.activeCall) {
          this.$store.commit('Call/SET_CALL_STATUS', 'voicemail');
        }
        return;
      }
      this.missedTimeout = setTimeout(() => {
        if (this.callStatus === 'ringing' && this.activeCall) {
          this.$store.commit('Call/SET_CALL_STATUS', 'voicemail');
        }
      }, remaining);
    },
    clearMissedTimeout() {
      if (this.missedTimeout) {
        clearTimeout(this.missedTimeout);
        this.missedTimeout = null;
      }
      sessionStorage.removeItem('call_ring_start');
    },
  },
  beforeDestroy() {
    this.stopTimer();
    this.clearMissedTimeout();
  },
};
</script>

<style lang="scss" scoped>
.acm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}

.acm-card {
  background: #1c1c1e;
  border-radius: 24px;
  padding: 40px 32px 32px;
  min-width: 300px;
  max-width: 360px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.acm-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 40px;
}

.acm-avatar {
  border: 2px solid rgba(255, 255, 255, 0.15);
}

.acm-name {
  font-size: 22px;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.3px;
}

.acm-status {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
}

.acm-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  margin-bottom: 20px;
}

.acm-ctrl-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 16px;
  padding: 14px 18px;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s;
  &:hover { background: rgba(255, 255, 255, 0.18); }
  &.active { background: #fff; color: #1c1c1e; }
}

.acm-ctrl-label {
  font-size: 11px;
  font-weight: 500;
}

.acm-end-btn {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #ff3b30;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.15s;
  &:hover { transform: scale(1.08); }
  &:active { transform: scale(0.95); }
}

.acm-timer {
  font-size: 16px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  font-variant-numeric: tabular-nums;
}
</style>
